import React, { useState, useMemo } from "react";
import { Download, FileSpreadsheet, BarChart2, Clock, TrendingUp, Users, Timer } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};
const COLORS = ["#00ff90","#60a5fa","#c084fc","#fbbf24","#f87171","#34d399","#fb923c"];

const REPORT_TYPES = [
  { key: "by_category",  label: "By Category",      icon: BarChart2,  desc: "Volume per category"          },
  { key: "by_priority",  label: "By Priority",       icon: TrendingUp, desc: "Distribution across P1–P4"   },
  { key: "by_agent",     label: "By Agent",          icon: Users,      desc: "Tickets per agent"            },
  { key: "sla",          label: "SLA Compliance",    icon: Clock,      desc: "Breach rate per priority"     },
  { key: "aging",        label: "Ticket Aging",      icon: Timer,      desc: "Open >3, >7, >14 days"        },
];

function deriveLocal(tickets, reportType) {
  if (!tickets?.length) return { chart: [], rows: [] };

  if (reportType === "by_category") {
    const m = {};
    tickets.forEach(t => { m[t.category] = (m[t.category] || 0) + 1; });
    const chart = Object.entries(m).map(([label, value]) => ({ label, value }));
    return { chart, rows: chart.map(r => ({ category: r.label, count: r.value })) };
  }
  if (reportType === "by_priority") {
    const m = { P1: 0, P2: 0, P3: 0, P4: 0 };
    tickets.forEach(t => { if (m[t.priority] !== undefined) m[t.priority]++; });
    const chart = Object.entries(m).map(([label, value]) => ({ label, value }));
    return { chart, rows: chart.map(r => ({ priority: r.label, count: r.value })) };
  }
  if (reportType === "by_agent") {
    const m = {};
    tickets.forEach(t => { const a = t.assignee || "Unassigned"; m[a] = (m[a] || 0) + 1; });
    const chart = Object.entries(m).map(([label, value]) => ({ label, value }));
    return { chart, rows: chart.map(r => ({ agent: r.label, count: r.value })) };
  }
  if (reportType === "sla") {
    const m = { P1: { total: 0, breached: 0 }, P2: { total: 0, breached: 0 }, P3: { total: 0, breached: 0 }, P4: { total: 0, breached: 0 } };
    tickets.forEach(t => { if (m[t.priority]) { m[t.priority].total++; if (t.breached) m[t.priority].breached++; } });
    const chart = Object.entries(m).map(([label, d]) => ({ label, value: d.total ? Math.round((d.breached / d.total) * 100) : 0 }));
    return {
      chart,
      rows: Object.entries(m).map(([priority, d]) => ({
        priority, total: d.total, breached: d.breached,
        breach_pct: d.total ? `${Math.round((d.breached/d.total)*100)}%` : "0%"
      }))
    };
  }
  if (reportType === "aging") {
    const now = Date.now();
    const open = tickets.filter(t => t.status !== "Closed" && t.status !== "On Hold");
    const buckets = { "0–3 days": 0, "4–7 days": 0, "8–14 days": 0, ">14 days": 0 };
    const rows = open.map(t => {
      const days = Math.floor((now - new Date(t.created).getTime()) / 86400000);
      let bucket;
      if (days <= 3)       { bucket = "0–3 days";   buckets["0–3 days"]++;   }
      else if (days <= 7)  { bucket = "4–7 days";   buckets["4–7 days"]++;   }
      else if (days <= 14) { bucket = "8–14 days";  buckets["8–14 days"]++;  }
      else                 { bucket = ">14 days";   buckets[">14 days"]++;   }
      return { id: t.id, subject: t.subject, assignee: t.assignee || "—", priority: t.priority, days_open: days, age_bucket: bucket };
    }).sort((a, b) => b.days_open - a.days_open);
    const chart = Object.entries(buckets).map(([label, value]) => ({ label, value }));
    return { chart, rows };
  }
  return { chart: [], rows: [] };
}

export default function TicketReports({ rows: localTickets = [] }) {
  const [reportType, setReportType] = useState("by_category");
  const [filters,    setFilters]    = useState({ from: "", to: "", dept: "ALL", category: "ALL" });
  const [apiData,    setApiData]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [exporting,  setExporting]  = useState(null);
  const token = localStorage.getItem("token");
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const run = async () => {
    setLoading(true); setError(null); setApiData(null);
    try {
      const qs = new URLSearchParams({ ...filters, report: reportType }).toString();
      const res = await fetch(`${API}/api/admin/tickets/reports?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setApiData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const qs = new URLSearchParams({ ...filters, report: reportType, format }).toString();
      const res = await fetch(`${API}/api/admin/tickets/reports/export?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `tickets_${reportType}.${format === "excel" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExporting(null); }
  };

  const localDerived = useMemo(() => deriveLocal(localTickets, reportType), [localTickets, reportType]);
  const chart      = apiData?.chart      || localDerived.chart;
  const tableRows  = apiData?.rows       || localDerived.rows;

  const exportLocal = () => {
    if (!tableRows.length) return;
    const keys = Object.keys(tableRows[0]);
    const text = keys.join(",") + "\n" + tableRows.map(r => keys.map(k => r[k]).join(",")).join("\n");
    const blob = new Blob([text], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `tickets_${reportType}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  // Aging highlights
  const agingAlerts = reportType === "aging" && tableRows.filter(r => r.days_open >= 7);

  return (
    <div className="space-y-5">

      {/* Report type selector */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {REPORT_TYPES.map(({ key, label, icon: Icon, desc }) => (
          <button key={key} onClick={() => { setReportType(key); setApiData(null); }}
            className={`text-left rounded-xl border p-4 transition-all ${
              reportType === key ? "border-[#00ff90]/40 bg-[#00ff90]/8" : "border-white/5 bg-white/3 hover:border-[#00ff90]/20"
            }`}>
            <Icon size={15} className={reportType === key ? "text-[#00ff90]" : "text-white/30"} />
            <p className={`text-xs font-medium mt-2 ${reportType === key ? "text-[#00ff90]" : "text-white/60"}`}>{label}</p>
            <p className="text-xs text-white/20 mt-0.5">{desc}</p>
          </button>
        ))}
      </div>

      {/* Aging summary chips */}
      {reportType === "aging" && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: ">3 days", count: localDerived.rows.filter(r => r.days_open > 3).length,  color: "#fbbf24" },
            { label: ">7 days", count: localDerived.rows.filter(r => r.days_open > 7).length,  color: "#fb923c" },
            { label: ">14 days",count: localDerived.rows.filter(r => r.days_open > 14).length, color: "#f87171" },
            { label: "Total open",count: localDerived.rows.length,                              color: "#00ff90" },
          ].map(({ label, count, color }) => (
            <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
              <p className="text-xl font-bold" style={{ color }}>{count}</p>
              <p className="text-xs text-white/30 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className={`${card} p-5`}>
        <div className="flex flex-wrap gap-3 items-end">
          {reportType !== "aging" && (
            <>
              <div><label className={labelCls}>From</label><input type="date" className={inputCls} value={filters.from} onChange={e => set("from", e.target.value)} /></div>
              <div><label className={labelCls}>To</label><input type="date" className={inputCls} value={filters.to} onChange={e => set("to", e.target.value)} /></div>
            </>
          )}
          <div><label className={labelCls}>Department</label>
            <select className={inputCls} value={filters.dept} onChange={e => set("dept", e.target.value)}>
              {["ALL","Engineering","Product","QA","HR","Ops"].map(d => <option key={d} value={d}>{d === "ALL" ? "All Depts" : d}</option>)}
            </select></div>
          <button onClick={run} disabled={loading}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium self-end">
            {loading ? "Running…" : "Run Report"}
          </button>
          <button onClick={exportLocal}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors self-end">
            <Download size={12} /> CSV
          </button>
          {apiData && (
            <button onClick={() => exportReport("excel")} disabled={exporting !== null}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors self-end">
              <FileSpreadsheet size={12} /> Excel
            </button>
          )}
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {/* Chart */}
      {chart.length > 0 && (
        <div className={`${card} p-5`}>
          <p className="text-xs text-white/30 uppercase tracking-wider mb-3">{REPORT_TYPES.find(r => r.key === reportType)?.label}</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={chart} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
              <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} unit={reportType === "sla" ? "%" : ""} />
              <Tooltip {...tooltipStyle} formatter={v => [reportType === "sla" ? `${v}%` : v, ""]} />
              <Bar dataKey="value" radius={[4,4,0,0]}>
                {chart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} fillOpacity={0.8} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      {tableRows.length > 0 && (
        <div className={card}>
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">{REPORT_TYPES.find(r => r.key === reportType)?.label}</span>
            <span className="text-xs text-white/30">{tableRows.length} rows</span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0d0d0d]">
                <tr className="border-b border-white/5">
                  {Object.keys(tableRows[0]).map(k => (
                    <th key={k} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider whitespace-nowrap">{k.replace(/_/g," ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r, i) => (
                  <tr key={i} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${reportType === "aging" && r.days_open >= 14 ? "bg-red-500/3" : reportType === "aging" && r.days_open >= 7 ? "bg-orange-500/3" : ""}`}>
                    {Object.entries(r).map(([k, v], j) => (
                      <td key={j} className={`px-3 py-2.5 text-xs whitespace-nowrap ${
                        k === "days_open" && v >= 14 ? "text-red-400 font-bold" :
                        k === "days_open" && v >= 7  ? "text-orange-400 font-medium" :
                        "text-white/60"
                      }`}>{String(v ?? "—")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}